import { forwardRef, type ComponentProps, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { LuArrowUpRight, LuCircleHelp, LuCompass, LuLogOut, LuSparkles, LuUser } from 'react-icons/lu';
import { Box, HStack, Text, VStack, type MenuRootProps } from '@chakra-ui/react';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/avatar';
import {
    MenuRoot,
    MenuTrigger,
    MenuTriggerItem,
    MenuContent,
    MenuItem,
    MenuItemGroup,
    MenuSeparator,
} from '@/components/ui/menu';
import { useAuth } from '@/contexts/AuthContext';
import { useHasProductTour, startProductTour } from '@/features/product-tour';
import { ExternalUrls } from '@/lib/url-constants';
import { UserProfile } from '@/client';
import { route } from '@/routes/paths';
import { withAccountSettings } from '@/routes/account-settings';
import { SwitcherMenuBody, type SwitcherItem } from './navigation/SwitcherMenu';
import type { Workspace } from './navigation/useWorkspaceNav';

/** The account menu's own radii — a step rounder than the app's default menu. */
const CONTENT_RADIUS = '2xl';
const ITEM_RADIUS = 'lg';

interface AccountMenuProps {
    user: UserProfile;
    workspace?: Workspace | null;
    homes?: Workspace[];
    groups?: Workspace[];
    /** Rows between the account header and the account actions — mobile's overflow sections. */
    sections?: ReactNode;
    positioning?: MenuRootProps['positioning'];
    submenuPositioning?: MenuRootProps['positioning'];
    children?: ReactNode;
}

const DEFAULT_SUBMENU_POSITIONING: MenuRootProps['positioning'] = { placement: 'right-start', gutter: 8 };

export function AccountMenu({
    user,
    workspace = null,
    homes = [],
    groups = [],
    sections,
    positioning,
    submenuPositioning = DEFAULT_SUBMENU_POSITIONING,
    children,
}: AccountMenuProps) {
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const { signOut } = useAuth();
    const hasTour = useHasProductTour();

    const handleProfile = () => navigate(
        withAccountSettings(location.pathname, location.search),
        { state: { accountSettingsReturnTo: `${location.pathname}${location.search}` } },
    );

    const handleSignOut = () => {
        void (async () => {
            await signOut();
            navigate('/signin');
        })();
    };

    const accountLabel = intl.formatMessage({
        id: '0680d8aa-abc0-4f8d-8526-93083ff517eb',
        defaultMessage: 'Account menu',
    });

    const isHome = workspace?.kind === 'home';
    const switcherItems: SwitcherItem[] = workspace
        ? (isHome ? homes : groups).map((entry) => ({ id: entry.id, label: entry.name, meta: entry.meta }))
        : [];
    const handleSwitchWorkspace = (id: string) => navigate(isHome ? `/home/${id}` : `/groups/${id}`);
    const handleAddWorkspace = () => navigate(isHome ? '/onboarding' : '/onboarding?flow=group');
    const addWorkspaceLabel = intl.formatMessage(
        isHome
            ? { id: 'a5d719c0-4e83-4b26-9f01-7c3d5b8e2a64', defaultMessage: 'Add home' }
            : { id: '9c04e7b3-1f58-4a26-8d70-5b3c9e1a6f42', defaultMessage: 'Start a new group' },
    );

    const upgradeHref = workspace
        ? (isHome ? route('homeBilling', { homeId: workspace.id }) : route('groupBilling', { groupId: workspace.id }))
        : null;
    const handleUpgrade = () => {
        if (upgradeHref) navigate(upgradeHref);
    };

    return (
        <MenuRoot positioning={positioning}>
            <MenuTrigger asChild>
                {children ?? (
                    <Button
                        variant="ghost"
                        borderRadius="full"
                        aria-label={accountLabel}
                        p={0}
                        minW="auto"
                        h="auto"
                    >
                        <Avatar size="xs" name={user.name ?? ''} src={user.avatar_url ?? undefined} />
                    </Button>
                )}
            </MenuTrigger>

            <MenuContent minW="60" borderRadius={CONTENT_RADIUS}>
                {workspace ? (
                    <MenuRoot positioning={submenuPositioning}>
                        <MenuTriggerItem value="workspace-switch" fontWeight="bold" borderRadius={ITEM_RADIUS}>
                            <HStack gap="9px" flex={1} minW={0}>
                                <Avatar size="xs" name={user.name ?? ''} src={user.avatar_url ?? undefined} />
                                <VStack gap={0} align="start" minW={0} flex={1}>
                                    <Text truncate maxW="full">
                                        {user.name}
                                    </Text>
                                    <Text fontSize="xs" lineHeight="short" fontWeight="medium" color="fg.muted" truncate maxW="full">
                                        {workspace.name}
                                    </Text>
                                </VStack>
                            </HStack>
                        </MenuTriggerItem>
                        <MenuContent minW="264px" maxW="min(320px, calc(100vw - 24px))" borderRadius={CONTENT_RADIUS}>
                            <MenuItemGroup title={user.email}>
                                <SwitcherMenuBody
                                    currentId={workspace.id}
                                    items={switcherItems}
                                    onSelect={handleSwitchWorkspace}
                                    addLabel={addWorkspaceLabel}
                                    onAdd={handleAddWorkspace}
                                />
                            </MenuItemGroup>
                        </MenuContent>
                    </MenuRoot>
                ) : (
                    <MenuItem value="account" cursor="default" pointerEvents="none" fontWeight="bold" borderRadius={ITEM_RADIUS}>
                        <HStack gap="9px" minW={0}>
                            <Avatar size="xs" name={user.name ?? ''} src={user.avatar_url ?? undefined} />
                            <VStack gap={0} align="start" minW={0}>
                                <Text textOverflow="ellipsis" overflow="hidden">
                                    {user.name}
                                </Text>
                                <Text fontSize="xs" lineHeight="short" color="fg.muted" textOverflow="ellipsis" overflow="hidden">
                                    {user.email}
                                </Text>
                            </VStack>
                        </HStack>
                    </MenuItem>
                )}
                <MenuSeparator />

                {sections && (
                    <>
                        {sections}
                        <MenuSeparator />
                    </>
                )}

                {upgradeHref && (
                    <>
                        <MenuItem value="upgrade" onClick={handleUpgrade} cursor="button" borderRadius={ITEM_RADIUS}>
                            <LuSparkles />
                            <FormattedMessage id="fe80ebcd-068b-472b-87e3-db41562ac984" defaultMessage="Upgrade plan" />
                        </MenuItem>
                    </>
                )}

                <MenuItem value="profile" onClick={handleProfile} cursor="button" borderRadius={ITEM_RADIUS}>
                    <LuUser />
                    <FormattedMessage id="fbe6aa32-bf4b-40fd-9c02-dba1e5696684" defaultMessage="Account Settings" />
                </MenuItem>

                <MenuSeparator />
                <MenuRoot positioning={submenuPositioning}>
                    <MenuTriggerItem value="help" borderRadius={ITEM_RADIUS}>
                        <LuCircleHelp />
                        <FormattedMessage id="9b0d5a72-1e38-4c94-a6f5-70d2b8e3c145" defaultMessage="Help" />
                    </MenuTriggerItem>
                    <MenuContent minW="48" borderRadius={CONTENT_RADIUS}>
                        {hasTour && (
                            <MenuItem value="tour" onClick={startProductTour} cursor="button" borderRadius={ITEM_RADIUS}>
                                <LuCompass />
                                <FormattedMessage
                                    id="f456e04f-0f95-4aab-a7a7-6213b2688e1d"
                                    defaultMessage="Take the tour"
                                />
                            </MenuItem>
                        )}
                        <MenuItem value="support" asChild cursor="button" borderRadius={ITEM_RADIUS}>
                            <a href={`mailto:${ExternalUrls.SUPPORT_EMAIL}`}>
                                <FormattedMessage id="c62a7e04-9d18-4f53-8b06-3e7c1b5a9d02" defaultMessage="Support" />
                            </a>
                        </MenuItem>
                        <MenuSeparator />
                        <MenuItem value="terms" asChild cursor="button" borderRadius={ITEM_RADIUS}>
                            <ExternalMenuLink href={ExternalUrls.TERMS_OF_SERVICE}>
                                <FormattedMessage id="7e15c3d0-8b46-4a92-b0f7-2c9d5e1a6b34" defaultMessage="Terms" />
                            </ExternalMenuLink>
                        </MenuItem>
                        <MenuItem value="privacy" asChild cursor="button" borderRadius={ITEM_RADIUS}>
                            <ExternalMenuLink href={ExternalUrls.PRIVACY_POLICY}>
                                <FormattedMessage id="4d7a7cef-cace-43b2-bcf2-21cb728bae27" defaultMessage="Privacy" />
                            </ExternalMenuLink>
                        </MenuItem>
                    </MenuContent>
                </MenuRoot>

                <MenuItem value="logout" color="fg.error" onClick={handleSignOut} cursor="button" borderRadius={ITEM_RADIUS}>
                    <LuLogOut />
                    <FormattedMessage id="dccab016-30fc-4f5a-922a-075f28c70d9e" defaultMessage="Log out" />
                </MenuItem>
            </MenuContent>
        </MenuRoot>
    );
}

/**
 * An `<a>` for `MenuItem asChild` that still reads as a menu item.
 *
 * `asChild` clones the item's role, highlight state and handlers onto its
 * immediate child; a plain `<a>{text}</a>` picks those up for free, but this
 * one needs its own markup for the trailing arrow, so it has to forward
 * whatever lands on it (`{...rest}`) onto the real anchor itself.
 */
const ExternalMenuLink = forwardRef<HTMLAnchorElement, ComponentProps<'a'>>(
    function ExternalMenuLink({ children, ...rest }, ref) {
        return (
            <a ref={ref} target="_blank" rel="noopener noreferrer" {...rest}>
                <HStack justify="space-between" flex={1} minW={0}>
                    <Text>{children}</Text>
                    <Box color="fg.muted" display="flex" flexShrink={0}>
                        <LuArrowUpRight size={13} />
                    </Box>
                </HStack>
            </a>
        );
    },
);
